import React, { useEffect, useRef, useState } from "react";
import alertService from "../services/alertService";
import detectionService from "../services/detectionService";
import RulesDrawer from "./RulesDrawer";
import {
  isPushSupported,
  getPushSubscriptionState,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
} from "../services/pushService";
import "./AlertsTab.css";

const AlertsTab = ({ highlightEventId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMessage, setPushMessage] = useState(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [detectionEvents, setDetectionEvents] = useState([]);
  const [detectionLoading, setDetectionLoading] = useState(false);
  const [detectionError, setDetectionError] = useState(null);
  const [activeRecording, setActiveRecording] = useState(null);
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [recordingError, setRecordingError] = useState(null);
  const [highlightedId, setHighlightedId] = useState(null);
  const [clearingAll, setClearingAll] = useState(false);
  const videoRef = useRef(null);
  const itemRefs = useRef({});

  const loadEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await alertService.getPowerEvents();
      setEvents(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDetections = async () => {
    setDetectionLoading(true);
    setDetectionError(null);
    try {
      const data = await detectionService.getEvents({ limit: 50 });
      const fetched = Array.isArray(data) ? data : [];
      setDetectionEvents((prev) => {
        if (!highlightEventId) return fetched;
        const id = Number(highlightEventId);
        if (fetched.some((e) => e.id === id)) return fetched;
        // Preserve an out-of-range highlighted event that was fetched separately
        // (e.g. older than the default limit) so it doesn't disappear from the list.
        const highlighted = prev.find((e) => e.id === id);
        return highlighted ? [highlighted, ...fetched] : fetched;
      });
    } catch (err) {
      setDetectionError(err.message);
    } finally {
      setDetectionLoading(false);
    }
  };

  const playDetection = async (event) => {
    setRecordingLoading(true);
    setRecordingError(null);
    setActiveRecording(null);
    try {
      const link = await detectionService.getRecordingLink(event.stream_id, event.detected_at);
      setActiveRecording({
        eventId: event.id,
        streamID: event.stream_id,
        detectedAt: event.detected_at,
        url: link.url,
        offset: link.offset_seconds || 0,
      });
    } catch (err) {
      setRecordingError(err.message);
    } finally {
      setRecordingLoading(false);
    }
  };

  const onVideoLoaded = () => {
    if (videoRef.current && activeRecording && activeRecording.offset > 0) {
      videoRef.current.currentTime = activeRecording.offset;
    }
  };

  const closeRecording = () => {
    setActiveRecording(null);
    setRecordingError(null);
  };

  const handleClearAll = async () => {
    if (!window.confirm("Remove all alerts? This deletes both alert history and camera detections and cannot be undone.")) {
      return;
    }
    setClearingAll(true);
    setError(null);
    setDetectionError(null);
    try {
      await Promise.all([
        alertService.deleteAllPowerEvents(),
        detectionService.deleteAllEvents(),
      ]);
      setEvents([]);
      setDetectionEvents([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setClearingAll(false);
    }
  };

  const refreshPushState = async () => {
    const supported = await isPushSupported();
    setPushSupported(supported);
    if (supported) {
      const existing = await getPushSubscriptionState();
      setPushSubscribed(!!existing);
    }
  };

  useEffect(() => {
    loadEvents();
    loadDetections();
    refreshPushState();
  }, []);

  useEffect(() => {
    if (!highlightEventId) return;
    const id = Number(highlightEventId);

    const scrollToEvent = () => {
      const el = itemRefs.current[id];
      if (!el) return false;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedId(id);
      // Snapshot images above the target load asynchronously and can shift the
      // layout after the initial scroll, pushing the target out of view.
      // Re-center a few times as those images finish loading.
      const reScrollTimers = [250, 600, 1000, 1600].map((delay) =>
        setTimeout(() => {
          const target = itemRefs.current[id];
          if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
        }, delay)
      );
      const highlightTimer = setTimeout(
        () => setHighlightedId((current) => (current === id ? null : current)),
        3000
      );
      return () => {
        clearTimeout(highlightTimer);
        reScrollTimers.forEach(clearTimeout);
      };
    };

    if (scrollToEvent()) return;

    // Event isn't in the currently loaded list (e.g. older than the default limit) — fetch it directly.
    let cancelled = false;
    detectionService
      .getEvents({ id })
      .then((data) => {
        if (cancelled || !Array.isArray(data) || data.length === 0) return;
        setDetectionEvents((prev) => {
          if (prev.some((e) => e.id === id)) return prev;
          return [...data, ...prev];
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [highlightEventId, detectionEvents.length]);

  const handlePushToggle = async () => {
    setPushBusy(true);
    setPushMessage(null);
    try {
      if (pushSubscribed) {
        await unsubscribeFromPushNotifications();
        setPushMessage("Push notifications disabled");
      } else {
        await subscribeToPushNotifications();
        setPushMessage("Push notifications enabled");
      }
    } catch (err) {
      setPushMessage(err.message);
    } finally {
      await refreshPushState();
      setPushBusy(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "--";
    return new Date(value).toLocaleString();
  };

  const getEventClass = (type) => {
    if (type === "blackout") return "alert-blackout";
    if (type === "restored") return "alert-restored";
    return "";
  };

  const getEventLabel = (type) => {
    if (type === "blackout") return "Blackout";
    if (type === "restored") return "Restored";
    if (type === "motion") return "Motion detected";
    return type ? type.charAt(0).toUpperCase() + type.slice(1) : "Unknown";
  };

  const getDetectionClass = (type) => {
    if (type === "motion") return "alert-motion";
    if (["person", "car", "bicycle", "motorcycle", "bus", "truck"].includes(type)) return "alert-object";
    return "";
  };

  return (
    <div className="tab-content">
      <div className="alerts-tab-header">
        <h2>Alerts</h2>
        {(events.length > 0 || detectionEvents.length > 0) && (
          <button
            type="button"
            className="alerts-tab-clear-all"
            onClick={handleClearAll}
            disabled={clearingAll}
          >
            {clearingAll ? "Removing..." : "Remove All"}
          </button>
        )}
      </div>

      <div className="alerts-tab-push-section">
        <div className="alerts-tab-push-row">
          <div className="alerts-tab-push-info">
            <strong>Push Notifications</strong>
            <span className="alerts-tab-push-status">
              {!pushSupported ? "Not supported" : pushSubscribed ? "Enabled" : "Disabled"}
            </span>
          </div>
          {pushSupported && (
            <button
              type="button"
              className={`alerts-tab-push-toggle ${pushSubscribed ? "on" : "off"}`}
              onClick={handlePushToggle}
              disabled={pushBusy}
            >
              {pushBusy ? "..." : pushSubscribed ? "On" : "Off"}
            </button>
          )}
        </div>
        {pushMessage && <div className="alerts-tab-push-message">{pushMessage}</div>}

        <button
          type="button"
          className="alerts-tab-rules-btn"
          onClick={() => setRulesOpen(true)}
        >
          <svg viewBox="0 0 24 24"><path d="M12 8V4l8 8-8 8v-4H4V8h8Z" /></svg>
          Manage Rules
        </button>
      </div>

      <div className="alerts-tab-section-title">
        <span>Alert History</span>
        <button
          type="button"
          className="alerts-tab-refresh"
          onClick={loadEvents}
          disabled={loading}
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error && <div className="tab-error">{error}</div>}

      <div className="alerts-tab-list">
        {events.length === 0 && !loading && !error && (
          <div className="tab-empty">No alerts yet.</div>
        )}
        {events.map((event) => (
          <div
            key={event.ID || event.id}
            className={`alerts-tab-item ${getEventClass(event.Type || event.type)}`}
          >
            <div className="alerts-tab-item-main">
              <span className="alerts-tab-item-type">
                {getEventLabel(event.Type || event.type)}
              </span>
              <span className="alerts-tab-item-voltage">
                {(event.GridVoltage || event.gridVoltage || 0).toFixed(2)} V
              </span>
            </div>
            <div className="alerts-tab-item-time">
              {formatDate(event.StartedAt || event.startedAt)}
              {event.EndedAt || event.endedAt
                ? ` — ${formatDate(event.EndedAt || event.endedAt)}`
                : " — ongoing"}
            </div>
          </div>
        ))}
      </div>

      <div className="alerts-tab-section-title">
        <span>Camera Detections</span>
        <button
          type="button"
          className="alerts-tab-refresh"
          onClick={loadDetections}
          disabled={detectionLoading}
        >
          {detectionLoading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {recordingError && <div className="tab-error">{recordingError}</div>}

      {detectionError && <div className="tab-error">{detectionError}</div>}

      <div className="alerts-tab-list">
        {detectionEvents.length === 0 && !detectionLoading && !detectionError && (
          <div className="tab-empty">No detection events yet.</div>
        )}
        {detectionEvents.map((event) => (
          <div
            key={event.id}
            ref={(el) => {
              itemRefs.current[event.id] = el;
            }}
            className={`alerts-tab-item ${getDetectionClass(event.type)} ${
              highlightedId === event.id ? "alerts-tab-item-highlighted" : ""
            }`}
          >
            <div className="alerts-tab-item-main">
              <span className="alerts-tab-item-type">{getEventLabel(event.type)}</span>
              <span className="alerts-tab-item-voltage">
                {(event.confidence * 100).toFixed(0)}%
              </span>
            </div>
            <div className="alerts-tab-item-time">{formatDate(event.detected_at)}</div>
            {activeRecording && activeRecording.eventId === event.id ? (
              <div className="alerts-tab-player alerts-tab-player-inline">
                <div className="alerts-tab-player-header">
                  <strong>{activeRecording.streamID}</strong>
                  <span>{formatDate(activeRecording.detectedAt)}</span>
                  <button type="button" onClick={closeRecording}>Close</button>
                </div>
                <video
                  ref={videoRef}
                  controls
                  autoPlay
                  playsInline
                  preload="metadata"
                  src={activeRecording.url}
                  onLoadedMetadata={onVideoLoaded}
                />
              </div>
            ) : event.snapshot_url ? (
              <img
                src={event.snapshot_url}
                alt="Detection snapshot"
                className="alerts-tab-snapshot"
              />
            ) : null}
            <div className="alerts-tab-item-actions">
              {activeRecording && activeRecording.eventId === event.id ? (
                <button type="button" onClick={closeRecording}>Close</button>
              ) : (
                <button type="button" onClick={() => playDetection(event)} disabled={recordingLoading}>
                  {recordingLoading ? "Loading..." : "Play recording"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <RulesDrawer open={rulesOpen} onClose={() => setRulesOpen(false)} />
    </div>
  );
};

export default AlertsTab;
